import { SSRModalCardSelection } from '@fz/ui-components-react';
import { SsrType } from '@models/common/shared-enums.model';
import type { SpecialServiceResponseMapped } from '@models/landing/api/special-service.model';
import type { CodeTypeList } from '@models/master-data/master-data.model';
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useMasterDataStore,
  selectCodeTypeList,
  selectPreferredServices,
} from '@store/master-data.store';
import { IconClose } from '@shared/icons';
import {
  mapLoungeToModalView,
  type LoungeAddApiPayload,
} from '@shared/components/LoungeSsrModalComponent/lounge.view.mapper';
import {
  findNextSelectablePassengerId,
  isInfantPassengerTypeLabel,
  countNonInfantPassengers,
  resolveDefaultSelectablePassengerId,
  resolvePassengerTypeLabel,
} from '@shared/components/ssr-passenger.util';
import {
  isMatchingLoungeTab,
  parseLoungeAmount,
  resolveApplicableOrPurchasedLoungeInfo,
  resolveCurrencyDecimalPlaces,
  resolveLoungeTabValue,
  resolveLoungeCodeItem,
  resolveLoungeInfoPurchased,
  resolveLoungeTabUnavailableReason,
  resolvePassengerHasPurchasedLounge,
  resolveUniqueLoungeQuotes,
  type LoungeTabUnavailableReason,
} from '@shared/components/LoungeSsrModalComponent/lounge-options.util';
import { getResource } from '@utils/resource-translator.util';
import { PASSENGER_NAVIGATION_DELAY } from '@utils/passenger-navigation.util';

interface LoungeProps {
  specialService?: SpecialServiceResponseMapped;
  currencyDecimalPlaces?: number;
  onConfirmSelection?: (payloads: LoungeAddApiPayload[], totalAmount?: number) => void;
  onSelectionAmountChange?: (amount: number) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

type LoungeTabPassengerSelectionMap = Record<string, Record<string, string>>;
type LoungeModalOption = ReturnType<typeof mapLoungeToModalView>['options'][number];
type LoungePassengerDetails = NonNullable<
  ReturnType<typeof mapLoungeToModalView>['passengerSSRDetails']
>;

const ALL_PASSENGERS_ID = 'all-passengers';
const LOUNGE_SSR_TYPE = String(SsrType.LOUNGE);
const SAME_FOR_ALL_CHECKBOX_ID = 'same-for-all-checkbox';
const SSR_CLOSE_ICON = <IconClose size={27} aria-hidden={true} />;

/**
 * SSRModalCardSelection owns an uncontrolled same-for-all checkbox (internal useState(false)).
 * After confirm/reopen the modal remounts unchecked, so sync the DOM tick to parent state.
 */
const syncSameForAllCheckbox = (shouldBeChecked: boolean): void => {
  const checkbox = document.getElementById(SAME_FOR_ALL_CHECKBOX_ID) as HTMLInputElement | null;

  if (!checkbox || checkbox.checked === shouldBeChecked) {
    return;
  }

  checkbox.click();
};

const buildLoungeOptionId = (codeType: string): string => `lounge-${codeType}`;

const extractCodeTypeFromOptionId = (optionId: string): string => optionId.replace(/^lounge-/, '');

const buildSelectionMap = (
  specialService?: SpecialServiceResponseMapped,
): LoungeTabPassengerSelectionMap => {
  if (!specialService) {
    return {};
  }

  const uniqueTabValues = [
    ...new Set(
      resolveUniqueLoungeQuotes(specialService).map((quote) =>
        resolveLoungeTabValue(quote.logicalFlightId, quote.physicalFlightId),
      ),
    ),
  ];

  return uniqueTabValues.reduce<LoungeTabPassengerSelectionMap>((tabAcc, tabValue) => {
    tabAcc[tabValue] = specialService.passengerList.reduce<Record<string, string>>(
      (paxAcc, passenger) => {
        // Exclude infants from selection map - they cannot select lounge
        if (!isInfantPassengerTypeLabel(resolvePassengerTypeLabel(passenger.passengerType))) {
          paxAcc[passenger.resPaxId.toString()] = '';
        }
        return paxAcc;
      },
      {},
    );
    return tabAcc;
  }, {});
};

// Helper: Extract resPaxId from SSR item
const extractResPaxId = (selectedItem: Record<string, unknown>): number | null => {
  const resPaxIdRaw = selectedItem.resPaxId ?? selectedItem.resPaxID;
  const resPaxId =
    typeof resPaxIdRaw === 'number' ? resPaxIdRaw : Number.parseInt(String(resPaxIdRaw ?? ''), 10);

  return Number.isFinite(resPaxId) ? resPaxId : null;
};

// Helper: Extract flight IDs from SSR item
const extractFlightIds = (
  selectedItem: Record<string, unknown>,
): { logicalFlightId: string; physicalFlightId: string } => {
  const logicalFlightId = String(
    selectedItem.logicalFlightId ?? selectedItem.logicalFlightID ?? '',
  );
  const physicalFlightId = String(
    selectedItem.physicalFlightId ?? selectedItem.physicalFlightID ?? '',
  );
  return { logicalFlightId, physicalFlightId };
};

// Helper: Parse SSR item for lounge selection
const parseSSRItemForLounge = (
  item: unknown,
): {
  codeType: string;
  logicalFlightId: string;
  physicalFlightId: string;
  resPaxId: number;
} | null => {
  if (typeof item !== 'object' || item === null) {
    return null;
  }

  const selectedItem = item as Record<string, unknown>;
  const ssrType = String(selectedItem.ssrType ?? '').toUpperCase();
  if (ssrType !== LOUNGE_SSR_TYPE) {
    return null;
  }

  const codeType = String(selectedItem.codeType ?? '');
  const resPaxId = extractResPaxId(selectedItem);

  if (!codeType || resPaxId === null) {
    return null;
  }

  const { logicalFlightId, physicalFlightId } = extractFlightIds(selectedItem);

  return { codeType, logicalFlightId, physicalFlightId, resPaxId };
};

const buildInitialSelectionMap = (
  specialService: SpecialServiceResponseMapped | undefined,
): LoungeTabPassengerSelectionMap => {
  const baseSelectionMap = buildSelectionMap(specialService);

  if (!specialService || specialService.selectedSSRList.length === 0) {
    return baseSelectionMap;
  }

  const nextSelectionMap: LoungeTabPassengerSelectionMap = Object.entries(baseSelectionMap).reduce(
    (acc, [tabKey, tabSelections]) => {
      acc[tabKey] = { ...tabSelections };
      return acc;
    },
    {} as LoungeTabPassengerSelectionMap,
  );

  specialService.selectedSSRList.forEach((item) => {
    const parsed = parseSSRItemForLounge(item);
    if (!parsed) {
      return;
    }

    const { codeType, logicalFlightId, physicalFlightId, resPaxId } = parsed;

    const matchingQuote = specialService.LoungeQuotes.find((quote) => {
      const sameLogicalFlight = logicalFlightId === '' || quote.logicalFlightId === logicalFlightId;
      const samePhysicalFlight =
        physicalFlightId === '' || quote.physicalFlightId === physicalFlightId;

      return sameLogicalFlight && samePhysicalFlight;
    });

    if (!matchingQuote) {
      return;
    }

    const tabKey = resolveLoungeTabValue(
      matchingQuote.logicalFlightId,
      matchingQuote.physicalFlightId,
    );
    const passengerId = resPaxId.toString();

    if (nextSelectionMap[tabKey]?.[passengerId] === undefined) {
      return;
    }

    nextSelectionMap[tabKey][passengerId] = buildLoungeOptionId(codeType);
  });

  return nextSelectionMap;
};

// Helper: Check if SSR item matches active flight tab
const matchesActiveFlightTab = (
  activeFlightTab: string,
  logicalFlightId: string,
  physicalFlightId: string,
): boolean => {
  if (activeFlightTab === '') {
    return true;
  }

  return (
    activeFlightTab === logicalFlightId ||
    activeFlightTab === physicalFlightId ||
    (logicalFlightId === '' && physicalFlightId === '')
  );
};

const hasSessionSelectedLoungeForPassenger = (
  specialService: SpecialServiceResponseMapped | undefined,
  activeFlightTab: string,
  selectedPassengerId: string,
): boolean => {
  if (!specialService) {
    return false;
  }

  const selectedResPaxId = Number(selectedPassengerId);

  return specialService.selectedSSRList.some((item) => {
    const parsed = parseSSRItemForLounge(item);
    if (!parsed) {
      return false;
    }

    if (parsed.resPaxId !== selectedResPaxId) {
      return false;
    }

    return matchesActiveFlightTab(activeFlightTab, parsed.logicalFlightId, parsed.physicalFlightId);
  });
};

const findOptionById = (
  options: LoungeModalOption[],
  selectedOptionId: string,
): LoungeModalOption | undefined => {
  return options.find((option) => option.id === selectedOptionId);
};

const resolveSelectedOptionId = (
  selectedOptionId: string | undefined,
  fallbackOptionId: string | undefined,
): string | undefined => {
  if (selectedOptionId && selectedOptionId.length > 0) {
    return selectedOptionId;
  }

  return fallbackOptionId;
};

// Helper: Determine if lounge option is purchased
const determinePurchasedStatus = (
  hasSessionSelectionForPassenger: boolean,
  selectedPassengerId: string,
  specialService: SpecialServiceResponseMapped | undefined,
  activeFlightTab: string,
  codeType: string,
): boolean => {
  if (selectedPassengerId === ALL_PASSENGERS_ID) {
    return resolveLoungeInfoPurchased(specialService, activeFlightTab, codeType);
  }

  const hasPurchasedLounge = resolvePassengerHasPurchasedLounge(
    specialService,
    activeFlightTab,
    selectedPassengerId,
    codeType,
  );

  if (hasPurchasedLounge) {
    return true;
  }

  if (hasSessionSelectionForPassenger) {
    return false;
  }

  return false;
};

const resolveOptionsForTab = (
  specialService: SpecialServiceResponseMapped | undefined,
  codeTypeList: CodeTypeList | undefined,
  activeFlightTab: string,
  selectedPassengerId: string | undefined,
  loungeViewOptions: LoungeModalOption[],
  currencyDecimalPlaces: number,
): LoungeModalOption[] => {
  const quotes = resolveUniqueLoungeQuotes(specialService);
  const quotesForTab = activeFlightTab
    ? quotes.filter((quote) =>
        isMatchingLoungeTab(activeFlightTab, quote.logicalFlightId, quote.physicalFlightId),
      )
    : quotes;
  const hasSessionSelectionForPassenger = hasSessionSelectedLoungeForPassenger(
    specialService,
    activeFlightTab,
    selectedPassengerId ?? '',
  );

  return quotesForTab.flatMap((quote) =>
    resolveApplicableOrPurchasedLoungeInfo(
      quote.LoungeInfo,
      specialService,
      activeFlightTab,
      selectedPassengerId,
    ).map((loungeInfo) => {
      const codeItem = resolveLoungeCodeItem(loungeInfo.codeType, codeTypeList);
      const totalAmount = parseLoungeAmount(loungeInfo.totalAmount);

      // Find the corresponding option from loungeView to get mostPopularBadgeText
      const matchingViewOption = loungeViewOptions.find(
        (opt) => opt.id === buildLoungeOptionId(loungeInfo.codeType),
      );

      const mostPopularBadgeText = matchingViewOption?.mostPopularBadgeText ?? undefined;

      return {
        id: buildLoungeOptionId(loungeInfo.codeType),
        label: codeItem?.shortCodeName ?? '',
        description: `${quote.currency} ${totalAmount.toFixed(currencyDecimalPlaces)}`,
        price: totalAmount,
        currency: quote.currency,
        icon: undefined,
        cardBadge: undefined,
        col: 'col-6',
        isPurchased: determinePurchasedStatus(
          hasSessionSelectionForPassenger,
          selectedPassengerId ?? '',
          specialService,
          activeFlightTab,
          loungeInfo.codeType,
        ),
        mostPopularBadgeText,
      };
    }),
  );
};

const resolveSelectedPriceForTab = (
  selectionByTabPassenger: LoungeTabPassengerSelectionMap,
  activeFlightTab: string,
  options: LoungeModalOption[],
): number => {
  const selectedOptionIds = Object.values(selectionByTabPassenger[activeFlightTab] ?? {});

  return selectedOptionIds.reduce<number>((total, selectedOptionId) => {
    if (!selectedOptionId) {
      return total;
    }

    return total + (findOptionById(options, selectedOptionId)?.price ?? 0);
  }, 0);
};

const resolvePassengerSsrDetails = (
  baseDetails: ReturnType<typeof mapLoungeToModalView>['passengerSSRDetails'],
  selectedByPassenger: Record<string, string>,
  codeTypeList: CodeTypeList | undefined,
): LoungePassengerDetails => {
  const details = { ...(baseDetails ?? {}) };

  Object.entries(selectedByPassenger).forEach(([passengerId, selectedOptionId]) => {
    const currentDetails = details[passengerId] ?? {};
    const hasSelectedOption = selectedOptionId !== '';
    details[passengerId] = {
      // When there is a session selection, hide purchased supporting text and show selected only.
      subText1: hasSelectedOption ? '' : (currentDetails.subText1 ?? ''),
      subText2:
        resolveLoungeCodeItem(extractCodeTypeFromOptionId(selectedOptionId), codeTypeList)
          ?.reviewDisplay ??
        currentDetails.subText2 ??
        '',
    };
  });

  return details;
};

const resolvePurchasedOptionId = (
  specialService: SpecialServiceResponseMapped | undefined,
  activeFlightTab: string,
  selectedPassengerId: string,
  options: LoungeModalOption[],
): string => {
  const selectedResPaxId = Number(selectedPassengerId);
  const selectedPassenger = specialService?.passengerList.find(
    (passenger) => passenger.resPaxId === selectedResPaxId,
  );

  if (!selectedPassenger) {
    return '';
  }

  const purchasedCodeTypes = new Set(
    selectedPassenger.flightsDetail
      .filter((flightDetail) =>
        isMatchingLoungeTab(
          activeFlightTab,
          flightDetail.logicalFlightId,
          flightDetail.physicalFlightId,
        ),
      )
      .flatMap((flightDetail) => flightDetail.includedExtrasList)
      .filter((extra) => extra.ssrType.toUpperCase() === LOUNGE_SSR_TYPE && extra.amount > 0)
      .map((extra) => extra.codeType),
  );

  if (purchasedCodeTypes.size === 0) {
    return '';
  }

  return (
    options.find((option) => purchasedCodeTypes.has(extractCodeTypeFromOptionId(option.id)))?.id ??
    ''
  );
};

const isInfantPassengerId = (
  passengers: ReturnType<typeof mapLoungeToModalView>['passengers'],
  passengerId: string,
): boolean => {
  const passenger = passengers.find((item) => item.id === passengerId);
  return isInfantPassengerTypeLabel(passenger?.type);
};

// Resolve unavailable title/description for the active tab, falling back to the generic
// "no lounge included" copy when a reason-specific translation is not configured.
const resolveLoungeUnavailableTexts = (
  reason: LoungeTabUnavailableReason | undefined,
  fallbackTitle: string | undefined,
  fallbackDescription: string | undefined,
): { title: string | undefined; description: string | undefined } => {
  if (reason === 'cut-off') {
    return {
      title: getResource('Extras.Lounge.CutOff.Title.Text'),
      description: getResource('Extras.Lounge.CutOff.Description.Text'),
    };
  }

  return { title: fallbackTitle, description: fallbackDescription };
};

const buildPayloadsForConfirm = (
  selectionByTabPassenger: LoungeTabPassengerSelectionMap,
  specialService: SpecialServiceResponseMapped | undefined,
): LoungeAddApiPayload[] => {
  if (!specialService) {
    return [];
  }

  const payloadByPassengerKey = new Map<string, LoungeAddApiPayload>();

  Object.entries(selectionByTabPassenger).forEach(([tabValue, selectionByPassenger]) => {
    Object.entries(selectionByPassenger).forEach(([passengerId, optionId]) => {
      if (!optionId) {
        return;
      }

      const quote = specialService.LoungeQuotes.find((quoteItem) =>
        isMatchingLoungeTab(tabValue, quoteItem.logicalFlightId, quoteItem.physicalFlightId),
      );
      if (!quote) {
        return;
      }

      const loungeInfo = quote.LoungeInfo.find(
        (info) => info.codeType === extractCodeTypeFromOptionId(optionId),
      );
      if (!loungeInfo) {
        return;
      }

      const applicablePassengers =
        passengerId === ALL_PASSENGERS_ID
          ? loungeInfo.passengerDetails
          : loungeInfo.passengerDetails.filter(
              (applicablePax) => applicablePax.resPaxId === Number(passengerId),
            );

      applicablePassengers.forEach((applicablePassenger) => {
        const payloadKey = `${quote.logicalFlightId}-${quote.physicalFlightId}-${applicablePassenger.resPaxId}`;
        payloadByPassengerKey.set(payloadKey, {
          ssrType: quote.ssrType,
          logicalFlightId: quote.logicalFlightId,
          physicalFlightId: quote.physicalFlightId,
          amount: loungeInfo.amount,
          paxJourneyId: applicablePassenger.paxJourneyId,
          resPaxId: applicablePassenger.resPaxId,
          recordNumber: applicablePassenger.recordNumber,
          quantityAvailable: loungeInfo.quantityAvailable,
          chargeComment: loungeInfo.chargeComment,
          codeType: loungeInfo.codeType,
          isBusiness: loungeInfo.isBusiness,
          secureHash: loungeInfo.secureHash,
          taxInfo: loungeInfo.taxInfo,
          totalAmount: loungeInfo.totalAmount,
        });
      });
    });
  });

  return Array.from(payloadByPassengerKey.values());
};

// eslint-disable-next-line max-lines-per-function, complexity
export const LoungeComponent = ({
  specialService,
  currencyDecimalPlaces,
  onConfirmSelection,
  onSelectionAmountChange,
  isOpen,
  setIsOpen,
}: LoungeProps): ReactElement => {
  const resolvedCurrencyDecimalPlaces = resolveCurrencyDecimalPlaces(currencyDecimalPlaces);
  const codeTypeList = useMasterDataStore(selectCodeTypeList);
  const preferredServices = useMasterDataStore(selectPreferredServices);
  const loungeView = mapLoungeToModalView(
    specialService,
    codeTypeList,
    preferredServices,
    resolvedCurrencyDecimalPlaces,
  );
  // Show modal if there are options OR if it's unavailable (to show unavailable message)
  const hasLounge = loungeView.options.length > 0 || loungeView.isUnavailable;
  const initialSelectedPassengerId = resolveDefaultSelectablePassengerId(loungeView.passengers);
  const initialActiveFlightTabValue = loungeView.flightTabItems?.[0]?.value ?? '';

  const [selectedPassenger, setSelectedPassenger] = useState(initialSelectedPassengerId);
  const [activeFlightTab, setActiveFlightTab] = useState(initialActiveFlightTabValue);
  const [isSameForAll, setIsSameForAll] = useState(false);
  const [confirmedIsSameForAll, setConfirmedIsSameForAll] = useState(false);
  const [selectionByTabPassenger, setSelectionByTabPassenger] =
    useState<LoungeTabPassengerSelectionMap>(() => buildInitialSelectionMap(specialService));
  const [confirmedSelectionByTabPassenger, setConfirmedSelectionByTabPassenger] =
    useState<LoungeTabPassengerSelectionMap>(() => buildInitialSelectionMap(specialService));

  const hasAnySelection = Object.values(selectionByTabPassenger).some((selectionByPassenger) =>
    Object.values(selectionByPassenger).some((selectedOptionId) => Boolean(selectedOptionId)),
  );

  useEffect(() => {
    const freshSelectionMap = buildInitialSelectionMap(specialService);
    setSelectedPassenger(initialSelectedPassengerId);
    setActiveFlightTab(initialActiveFlightTabValue);
    setIsSameForAll(false);
    setConfirmedIsSameForAll(false);
    setSelectionByTabPassenger(freshSelectionMap);
    setConfirmedSelectionByTabPassenger(freshSelectionMap);
  }, [specialService, initialActiveFlightTabValue, initialSelectedPassengerId]);

  useEffect(() => {
    if (!isOpen) {
      setIsSameForAll(false);
      return;
    }

    setSelectedPassenger(initialSelectedPassengerId);
    setActiveFlightTab(initialActiveFlightTabValue);
    setIsSameForAll(confirmedIsSameForAll);
  }, [isOpen, initialActiveFlightTabValue, initialSelectedPassengerId, confirmedIsSameForAll]);

  // Keep the DS checkbox tick in sync with retained isSameForAll (modal remounts unchecked).
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      syncSameForAllCheckbox(isSameForAll);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, isSameForAll]);

  useEffect(() => {
    if (!isSameForAll) {
      return;
    }

    const selectedOptionIdForCurrentPassenger =
      selectionByTabPassenger[activeFlightTab]?.[selectedPassenger] ?? '';

    setSelectionByTabPassenger((previous) => {
      const tabSelection = previous[activeFlightTab] ?? {};
      const replicatedSelection = Object.keys(tabSelection).reduce<Record<string, string>>(
        (accumulator, passengerId) => {
          accumulator[passengerId] = selectedOptionIdForCurrentPassenger;
          return accumulator;
        },
        {},
      );

      const hasDifference = Object.entries(tabSelection).some(
        ([passengerId, optionId]) => optionId !== replicatedSelection[passengerId],
      );

      if (!hasDifference) {
        return previous;
      }

      return {
        ...previous,
        [activeFlightTab]: replicatedSelection,
      };
    });
  }, [isSameForAll, activeFlightTab, selectedPassenger, selectionByTabPassenger]);

  const allOptionsForActiveTab = useMemo(
    () =>
      resolveOptionsForTab(
        specialService,
        codeTypeList,
        activeFlightTab,
        undefined,
        loungeView.options,
        resolvedCurrencyDecimalPlaces,
      ),
    [
      specialService,
      codeTypeList,
      activeFlightTab,
      loungeView.options,
      resolvedCurrencyDecimalPlaces,
    ],
  );

  const optionsForActiveTab = useMemo(
    () =>
      resolveOptionsForTab(
        specialService,
        codeTypeList,
        activeFlightTab,
        selectedPassenger,
        loungeView.options,
        resolvedCurrencyDecimalPlaces,
      ),
    [
      specialService,
      codeTypeList,
      activeFlightTab,
      selectedPassenger,
      loungeView.options,
      resolvedCurrencyDecimalPlaces,
    ],
  );

  const activeLoungeFlightTabItem = specialService?.loungeFlightTabItems.find(
    (item) =>
      item.value === activeFlightTab ||
      item.physicalFlightID === activeFlightTab ||
      item.logicalFlightID === activeFlightTab,
  );

  const activeTabUnavailableReason = useMemo(
    () =>
      resolveLoungeTabUnavailableReason(specialService, activeFlightTab, activeLoungeFlightTabItem),
    [specialService, activeFlightTab, activeLoungeFlightTabItem],
  );

  const isUnavailableForActiveTab =
    loungeView.isUnavailable === true || activeTabUnavailableReason !== undefined;

  const { title: unavailableTitleForActiveTab, description: unavailableDescriptionForActiveTab } =
    resolveLoungeUnavailableTexts(
      activeTabUnavailableReason,
      loungeView.unavailableTitle,
      loungeView.unavailableDescription,
    );

  const hasPurchasableOptionsForActiveTab = useMemo(() => {
    const passengerIdsForActiveTab = Object.keys(selectionByTabPassenger[activeFlightTab] ?? {});

    return passengerIdsForActiveTab.some((passengerId) => {
      const optionsForPassenger = resolveOptionsForTab(
        specialService,
        codeTypeList,
        activeFlightTab,
        passengerId,
        loungeView.options,
        resolvedCurrencyDecimalPlaces,
      );

      return optionsForPassenger.some((option) => option.isPurchased !== true);
    });
  }, [
    selectionByTabPassenger,
    activeFlightTab,
    specialService,
    codeTypeList,
    loungeView.options,
    resolvedCurrencyDecimalPlaces,
  ]);

  // Close-only when every leg is cut-off; if any leg still allows confirmation, keep the confirm button.
  const areAllLegsCutOff = useMemo(() => {
    if (loungeView.flightTabItems.length === 0) {
      return isUnavailableForActiveTab;
    }

    return loungeView.flightTabItems.every((tabItem) => {
      const matchingLoungeFlightTabItem = specialService?.loungeFlightTabItems.find(
        (item) =>
          item.value === tabItem.value ||
          item.physicalFlightID === tabItem.value ||
          item.logicalFlightID === tabItem.value,
      );

      return (
        resolveLoungeTabUnavailableReason(
          specialService,
          tabItem.value,
          matchingLoungeFlightTabItem,
        ) !== undefined
      );
    });
  }, [loungeView.flightTabItems, specialService, isUnavailableForActiveTab]);

  const shouldCloseInsteadOfConfirm =
    loungeView.isUnavailable === true || areAllLegsCutOff || !hasPurchasableOptionsForActiveTab;
  const modalConfirmButtonLabel = shouldCloseInsteadOfConfirm
    ? getResource('Close.Text')
    : loungeView.confirmButtonLabel;

  const allPassengersCard = useMemo(
    () => [
      {
        id: ALL_PASSENGERS_ID,
        name: `${getResource('Extras.AllPassengers.Text')} (${countNonInfantPassengers(loungeView.passengers)})`,
        type: '',
        seatNumber: '',
        pnrNumber: '',
      },
    ],
    [loungeView.passengers],
  );

  const infantPassengers = loungeView.passengers.filter((passenger) => {
    return isInfantPassengerTypeLabel(passenger.type);
  });
  const passengersForModal = isSameForAll
    ? [...allPassengersCard, ...infantPassengers]
    : loungeView.passengers;
  const selectedPassengerIdForModal = isSameForAll ? ALL_PASSENGERS_ID : selectedPassenger;
  const showSameForAllPassengers = countNonInfantPassengers(loungeView.passengers) > 1;

  const selectedOptionIdFromState =
    selectionByTabPassenger[activeFlightTab]?.[selectedPassenger] ?? '';
  const selectedOptionIdForAllPassengersFromState = Object.values(
    selectionByTabPassenger[activeFlightTab] ?? {},
  ).find((optionId) => Boolean(optionId));
  const purchasedOptionId = useMemo(
    () =>
      resolvePurchasedOptionId(
        specialService,
        activeFlightTab,
        selectedPassenger,
        optionsForActiveTab,
      ),
    [optionsForActiveTab, specialService, activeFlightTab, selectedPassenger],
  );
  // When in "same for all" mode, only show actively selected options, not purchased ones
  const selectedOptionId = isSameForAll
    ? (selectedOptionIdForAllPassengersFromState ?? '')
    : resolveSelectedOptionId(selectedOptionIdFromState, purchasedOptionId);
  const passengersTitle = getResource('SelectPassenger.Passengers.Text');
  const clearLabelText = getResource('Clear.Text');

  const selectedPrice = resolveSelectedPriceForTab(
    selectionByTabPassenger,
    activeFlightTab,
    allOptionsForActiveTab,
  );
  const totalLabelForModal =
    !isUnavailableForActiveTab && hasPurchasableOptionsForActiveTab
      ? getResource('Extras.Lounge.Total.Text')
      : '';
  const priceAmountForModal =
    !isUnavailableForActiveTab && hasPurchasableOptionsForActiveTab
      ? selectedPrice.toFixed(resolvedCurrencyDecimalPlaces)
      : '';

  const resolveTotalSelectedAmount = (): number => {
    return Object.entries(selectionByTabPassenger).reduce<number>(
      (tabsAcc, [tabValue, selectedByPassenger]) => {
        const optionsForTab = resolveOptionsForTab(
          specialService,
          codeTypeList,
          tabValue,
          undefined,
          loungeView.options,
          resolvedCurrencyDecimalPlaces,
        );
        const optionPriceById = optionsForTab.reduce<Record<string, number>>(
          (optionsAcc, option) => {
            optionsAcc[option.id] = Number(option.price) || 0;
            return optionsAcc;
          },
          {},
        );

        const tabAmount = Object.values(selectedByPassenger).reduce<number>(
          (passengerAcc, optionId) => {
            if (!optionId) {
              return passengerAcc;
            }

            return passengerAcc + (optionPriceById[optionId] ?? 0);
          },
          0,
        );

        return tabsAcc + tabAmount;
      },
      0,
    );
  };

  // Notify parent when selections change (to capture initial total)
  useEffect(() => {
    const totalAmount = resolveTotalSelectedAmount();
    onSelectionAmountChange?.(totalAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionByTabPassenger, resolvedCurrencyDecimalPlaces]);

  const passengerSsrDetails = useMemo(
    () =>
      resolvePassengerSsrDetails(
        loungeView.passengerSSRDetails,
        selectionByTabPassenger[activeFlightTab] ?? {},
        codeTypeList,
      ),
    [loungeView.passengerSSRDetails, selectionByTabPassenger, activeFlightTab, codeTypeList],
  );

  const passengerSsrDetailsForModal = useMemo(() => {
    if (!isSameForAll) {
      return passengerSsrDetails;
    }

    const selectedOption = selectedOptionId
      ? findOptionById(optionsForActiveTab, selectedOptionId)
      : undefined;

    const loungeDisplay = selectedOption
      ? (resolveLoungeCodeItem(extractCodeTypeFromOptionId(selectedOption.id), codeTypeList)
          ?.reviewDisplay ?? '')
      : '';

    const infantNotApplicableText = getResource('Extras.NotApplSpecialService.Tooltip.Text');

    return {
      [ALL_PASSENGERS_ID]: {
        subText1: loungeDisplay ? '' : getResource('Extras.NoLounge.Selected.Text'),
        subText2: loungeDisplay,
      },
      ...Object.fromEntries(
        infantPassengers.map((passenger) => {
          return [
            passenger.id,
            {
              subText1: '',
              subText2: '',
              infantSubText: infantNotApplicableText,
            },
          ];
        }),
      ),
    };
  }, [
    isSameForAll,
    passengerSsrDetails,
    selectedOptionId,
    optionsForActiveTab,
    codeTypeList,
    infantPassengers,
  ]);

  const updateSelectionForAllPassengers = (
    previous: LoungeTabPassengerSelectionMap,
    nextValue: string,
  ): LoungeTabPassengerSelectionMap => {
    const tabSelection = previous[activeFlightTab] ?? {};
    const nextTabSelection = Object.keys(tabSelection).reduce<Record<string, string>>(
      (accumulator, passengerId) => {
        accumulator[passengerId] = nextValue;
        return accumulator;
      },
      {},
    );

    return {
      ...previous,
      [activeFlightTab]: nextTabSelection,
    };
  };

  const updateSelectionForSinglePassenger = (
    previous: LoungeTabPassengerSelectionMap,
    nextValue: string,
  ): LoungeTabPassengerSelectionMap => {
    return {
      ...previous,
      [activeFlightTab]: {
        ...(previous[activeFlightTab] ?? {}),
        [selectedPassenger]: nextValue,
      },
    };
  };

  const navigateToNextPassenger = (): void => {
    const nextPassengerId = findNextSelectablePassengerId(loungeView.passengers, selectedPassenger);

    if (nextPassengerId) {
      setTimeout(() => {
        setSelectedPassenger(nextPassengerId);
      }, PASSENGER_NAVIGATION_DELAY);
    }
  };

  const handleOptionSelect = (optionId: string): void => {
    const currentValue = isSameForAll
      ? (Object.values(selectionByTabPassenger[activeFlightTab] ?? {}).find((value) => value) ?? '')
      : (selectionByTabPassenger[activeFlightTab]?.[selectedPassenger] ?? '');
    const nextValue = currentValue === optionId ? '' : optionId;

    setSelectionByTabPassenger((previous) => {
      if (isSameForAll) {
        return updateSelectionForAllPassengers(previous, nextValue);
      }

      return updateSelectionForSinglePassenger(previous, nextValue);
    });

    if (nextValue === '' || isSameForAll) {
      return;
    }

    navigateToNextPassenger();
  };

  const resetTransientSelectionState = (): void => {
    setSelectionByTabPassenger(confirmedSelectionByTabPassenger);
    setIsSameForAll(confirmedIsSameForAll);
    setSelectedPassenger(initialSelectedPassengerId);
    setActiveFlightTab(initialActiveFlightTabValue);
  };

  const handleConfirm = (): void => {
    if (!hasPurchasableOptionsForActiveTab) {
      // Even when no purchasable options exist, call onConfirmSelection with empty array
      // to signal deselection so the caller can fire the remove-SSR API if needed
      onConfirmSelection?.([], 0);
      setConfirmedSelectionByTabPassenger(selectionByTabPassenger);
      setConfirmedIsSameForAll(isSameForAll);
      setIsOpen(false);
      return;
    }

    const totalAmount = resolveTotalSelectedAmount();
    onSelectionAmountChange?.(totalAmount);

    const payloads = buildPayloadsForConfirm(selectionByTabPassenger, specialService);

    // Always call even with an empty array — empty signals full deselection so the
    // caller can fire the remove-SSR API.
    // Pass total as second parameter for delta calculation
    onConfirmSelection?.(payloads, totalAmount);

    setConfirmedSelectionByTabPassenger(selectionByTabPassenger);
    setConfirmedIsSameForAll(isSameForAll);
    setIsOpen(false);
  };

  const handleClearSelections = (): void => {
    setSelectionByTabPassenger(buildSelectionMap(specialService));
    setIsSameForAll(false);
  };

  const handleClearClick = (): void => {
    handleClearSelections();
  };

  const handleModalClose = (): void => {
    resetTransientSelectionState();
    setIsOpen(false);
  };

  if (!hasLounge) {
    return <></>;
  }

  return (
    <SSRModalCardSelection
      isOpen={isOpen}
      title={loungeView.title}
      closeIcon={SSR_CLOSE_ICON}
      passengersTitle={passengersTitle}
      passengers={passengersForModal}
      selectedPassengerId={selectedPassengerIdForModal}
      onPassengerSelect={(passengerId) => {
        if (passengerId === ALL_PASSENGERS_ID) {
          return;
        }

        if (isInfantPassengerId(loungeView.passengers, passengerId)) {
          return;
        }

        setSelectedPassenger(passengerId);
      }}
      onClose={handleModalClose}
      onConfirm={handleConfirm}
      priceAmount={priceAmountForModal}
      confirmButtonLabel={modalConfirmButtonLabel}
      currencyCode={loungeView.currencyCode}
      passengerSSRDetails={passengerSsrDetailsForModal}
      flightTabItems={loungeView.flightTabItems}
      activeFlightTabValue={activeFlightTab}
      onFlightTabChange={setActiveFlightTab}
      contentTitle={
        isUnavailableForActiveTab ? getResource('Lounge.Text') : loungeView.contentTitle
      }
      contentDescription={isUnavailableForActiveTab ? '' : loungeView.contentDescription}
      infoBannerText={isUnavailableForActiveTab ? '' : loungeView.infoBannerText}
      options={optionsForActiveTab}
      selectedOptionId={selectedOptionId}
      onSelectOption={handleOptionSelect}
      passengerTypeLabelProps={{ className: 'label5', variant: 'tertiary' }}
      disableInfants={true}
      showClearButton={hasAnySelection}
      clearLabel={clearLabelText}
      onClear={handleClearClick}
      showSameForAllPassengers={showSameForAllPassengers}
      sameForAllPassengersLabel={getResource('Extras.SameForAllPassengers.Text')}
      totalLabel={totalLabelForModal}
      onSameForAllPassengersChange={(checked) => {
        const shouldApplyForAll = checked === true;

        if (shouldApplyForAll) {
          const optionForAllPassengers =
            selectionByTabPassenger[activeFlightTab]?.[selectedPassenger] ?? '';

          setSelectionByTabPassenger((previous) => {
            const tabSelection = previous[activeFlightTab] ?? {};
            const nextTabSelection = Object.keys(tabSelection).reduce<Record<string, string>>(
              (accumulator, passengerId) => {
                accumulator[passengerId] = optionForAllPassengers;
                return accumulator;
              },
              {},
            );

            return {
              ...previous,
              [activeFlightTab]: nextTabSelection,
            };
          });
        }

        setIsSameForAll(shouldApplyForAll);
      }}
      isUnavailable={isUnavailableForActiveTab}
      unavailableTitle={unavailableTitleForActiveTab}
      unavailableDescription={unavailableDescriptionForActiveTab}
      unavailableVariant={loungeView.unavailableVariant}
      unavailableIcon={loungeView.unavailableIcon}
    />
  );
};
